import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectUser } from '@store/slices/authSlice';
import { selectCurrentPlan, selectSubscriptionLoading, selectSubscriptionErrorCode } from '@store/slices/subscriptionSlice';
import { USER_ROLES } from '@constants';
import PlanUpgradeModal from './PlanUpgradeModal';

const PlanSelectionGuard = ({ children }) => {
  const user = useSelector(selectUser);
  const currentPlan = useSelector(selectCurrentPlan);
  const loading = useSelector(selectSubscriptionLoading);
  const errorCode = useSelector(selectSubscriptionErrorCode);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [hasChecked, setHasChecked] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Reset check when user changes (new login)
  useEffect(() => {
    setHasChecked(false);
    setShowPlanModal(false);
    setIsInitialLoad(true);
  }, [user?.id]);

  // Set isInitialLoad to false after subscription data loads
  useEffect(() => {
    if (!loading) {
      setIsInitialLoad(false);
    }
  }, [loading]);

  useEffect(() => {
    // Only check for students
    if (user?.role !== USER_ROLES.STUDENT) {
      setHasChecked(true);
      return;
    }

    // Wait for subscription data to load
    if (loading) {
      return;
    }

    // Show modal if student has no active plan (after loading completes)
    if (!currentPlan && !hasChecked) {
      // console.log('✅ No active plan detected - Showing Plan Selection Modal');
      setShowPlanModal(true);
      setHasChecked(true);
    }
  }, [user, currentPlan, loading, hasChecked]);

  const handleModalClose = () => {
    // Don't allow closing without selecting a plan
    // User must choose a plan to continue
  };

  // Don't show modal during initial load or while loading
  if (isInitialLoad || loading) {
    return children;
  }

  // Show plan modal if student has no active plan
  if (user?.role === USER_ROLES.STUDENT && showPlanModal && !currentPlan) {
    return (
      <>
        {/* Render children in background (blurred) */}
        <div className="filter blur-sm pointer-events-none">
          {children}
        </div>
        
        {/* Mandatory Plan Selection Modal */}
        <PlanUpgradeModal 
          isOpen={true} 
          onClose={handleModalClose}
          mandatory={true}
        />
      </>
    );
  }

  return children;
};

export default PlanSelectionGuard;
